import { Avatar, Card, Flex, ActionIcon, Anchor } from '@mantine/core';
import { getInitials } from '../../helper/data-transform';
import { useUser } from '../../hooks';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeHigh, faVolumeXmark } from '@fortawesome/free-solid-svg-icons';
import useTextToSpeech from './useTextToSpeech';
import { DOCUMENTATION_URL } from './Chat';
import { useTranslation } from 'react-i18next';
import useTrackingChat from './chat.analytics';
import { SUPPORT_EMAIL } from '../../common/constants';

const MessageCard = ({
  replies,
  isUser,
}: {
  replies: string[];
  isUser: boolean;
}) => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const fullName = `${user?.firstName} ${user?.lastName}`;
  const initials = getInitials(fullName);
  const { track } = useTrackingChat();

  const cannotAnswer = t('chat.cannotAnswer', {
    documentationUrl: DOCUMENTATION_URL,
    supportEmail: SUPPORT_EMAIL,
  });

  const message = replies[0] || cannotAnswer;

  // Create a separate text-to-speech instance for each message
  const { isSpeaking, handleSpeak } = useTextToSpeech(message);

  return (
    <Flex gap={10} justify={isUser ? 'flex-start' : 'flex-end'}>
      {!isUser && <Avatar title={fullName}>{initials}</Avatar>}

      <Card
        bg="dark.9"
        mb="md"
        radius="md"
        style={{ width: 'fit-content', position: 'relative' }}
      >
        <ReactMarkdown
          components={{
            p: ({ children }) => (
              <span style={{ whiteSpace: 'pre-wrap' }}>{children}</span>
            ),
            pre: ({ children }) => (
              <pre style={{ overflow: 'scroll' }}>{children}</pre>
            ),
            a: ({ children, href }) => (
              <Anchor
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track('Chat Link Clicked', {
                    link_url: href || '',
                    source: 'empty-response',
                  });
                }}
              >
                {children}
              </Anchor>
            ),
          }}
        >
          {message}
        </ReactMarkdown>
      </Card>
      {isUser && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => handleSpeak(message)}
          title={isSpeaking ? 'Stop speaking' : 'Listen to text'}
        >
          <FontAwesomeIcon icon={isSpeaking ? faVolumeXmark : faVolumeHigh} />
        </ActionIcon>
      )}
    </Flex>
  );
};

export default MessageCard;
